import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { caForeclosures, CA_COUNTIES_CONFIG } from '../../mocks/caForeclosures';
import { MapSidebar } from './components/MapSidebar';
import { LeafletMap } from './components/LeafletMap';

const ALL_COUNTIES = Object.keys(CA_COUNTIES_CONFIG);

interface Filters {
  minTax: number;
  maxTax: number;
  propertyType: string;
  counties: Set<string>;
  status: string;
}

const INITIAL_FILTERS: Filters = {
  minTax: 0,
  maxTax: 50000,
  propertyType: 'All Types',
  counties: new Set(ALL_COUNTIES),
  status: 'All Status',
};

export default function CATaxForeclosuresPage() {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [focusCounty, setFocusCounty] = useState<string | null>(null);

  const filteredProperties = useMemo(() => {
    return caForeclosures.filter(p => {
      if (p.taxesOwed < filters.minTax || p.taxesOwed > filters.maxTax) return false;
      if (filters.propertyType !== 'All Types' && p.propertyType !== filters.propertyType) return false;
      if (!filters.counties.has(p.county)) return false;
      if (filters.status !== 'All Status' && p.status !== filters.status) return false;
      return true;
    });
  }, [filters]);

  const handleFocusHandled = useCallback(() => setFocusCounty(null), []);

  return (
    <div className="h-screen flex flex-col bg-[#0f172a] overflow-hidden">
      {/* Top Bar */}
      <div className="bg-[#111827] border-b border-gray-700/50 px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors text-sm cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            Back
          </Link>
          <div className="h-4 w-px bg-gray-700"></div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-2-fill text-amber-400"></i>
            </div>
            <h1 className="text-white font-bold text-base tracking-tight">CA Tax Foreclosures Map</h1>
            <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
              BETA
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* County legend pills */}
          <div className="hidden lg:flex items-center gap-1.5 flex-wrap">
            {ALL_COUNTIES.slice(0, 5).map(c => (
              <button
                key={c}
                onClick={() => {
                  if (!filters.counties.has(c)) return;
                  setFocusCounty(c);
                }}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap"
                style={{
                  borderColor: `${CA_COUNTIES_CONFIG[c].color}44`,
                  backgroundColor: filters.counties.has(c) ? `${CA_COUNTIES_CONFIG[c].color}18` : 'transparent',
                  color: filters.counties.has(c) ? CA_COUNTIES_CONFIG[c].color : '#6b7280',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: CA_COUNTIES_CONFIG[c].color }}
                ></span>
                {c.split(' ')[0]}
              </button>
            ))}
            <span className="text-gray-500 text-xs">+{ALL_COUNTIES.length - 5} more</span>
          </div>

          <div className="h-4 w-px bg-gray-700"></div>

          {/* Status legend */}
          <div className="flex items-center gap-3 text-xs">
            {[
              { label: 'Active', color: '#22c55e' },
              { label: 'Pending', color: '#f59e0b' },
              { label: 'Upcoming', color: '#06b6d4' },
              { label: 'Sold', color: '#9ca3af' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }}></span>
                <span className="text-gray-400">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="h-4 w-px bg-gray-700"></div>

          <div className="flex items-center gap-1.5 bg-[#1f2937] border border-gray-600 rounded-lg px-3 py-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-2-fill text-amber-400 text-xs"></i>
            </div>
            <span className="text-amber-400 font-bold text-sm">{filteredProperties.length}</span>
            <span className="text-gray-400 text-xs">shown</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <MapSidebar
          filters={filters}
          onFiltersChange={setFilters}
          filteredProperties={filteredProperties}
          onCountyFocus={setFocusCounty}
        />

        {/* Map */}
        <div className="flex-1 relative overflow-hidden">
          <LeafletMap
            properties={filteredProperties}
            focusCounty={focusCounty}
            onFocusHandled={handleFocusHandled}
          />

          {/* Map overlay: no results */}
          {filteredProperties.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-[#111827]/90 border border-gray-700 rounded-xl px-8 py-6 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-off-line text-gray-500 text-3xl"></i>
                </div>
                <div className="text-gray-300 font-semibold">No properties match your filters</div>
                <div className="text-gray-500 text-sm mt-1">Try adjusting the filters in the sidebar</div>
              </div>
            </div>
          )}

          {/* Map overlay: property count badge */}
          <div className="absolute top-4 right-4 bg-[#111827]/90 border border-gray-700 rounded-xl px-4 py-3 pointer-events-none">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Properties Shown</div>
            <div className="text-2xl font-bold text-amber-400">{filteredProperties.length}</div>
            <div className="text-xs text-gray-500">of {caForeclosures.length} total</div>
          </div>

          {/* Zoom hint */}
          <div className="absolute bottom-8 left-4 bg-[#111827]/80 border border-gray-700/50 rounded-lg px-3 py-2 pointer-events-none">
            <div className="text-xs text-gray-400">
              <i className="ri-zoom-in-line mr-1"></i>
              Click markers to view property details
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
